import './ie-order.scss';

import { Alert, notification } from 'antd';
import { createContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  OrderHistory,
  SystemNotifications
} from '@/components/Shared/entities';
import { ROUTES } from '@/components/Shared/routes/routes';
import { AllurLoader, CustomTab } from '@/components/Shared/ui';
import { DictTypes, SELECTED_BANK_STATUSES } from '@/constants';
import { safeKey } from '@/services/helpers/dict';
import { useAppDispatch, useAppSelector } from '@/store';
import { setHeaderConfig } from '@/store/app.slice';

import { useCheckDealerForLeasing, useLeasingRequestId } from '../hooks';
import { DraftFormResetButton, HeaderInfo, HorizontalInfo } from './entities';
import FinancingStep from './financing-step';
import { useFetchOrderData, useSelectedOffer } from './hooks';
import OfferStep from './offer-step';
import OrderStep from './order-step';
import { resetIeLeasingOrder, setActiveTabIndex } from './store';

export const CreateTabContext = createContext({ isFieldsFrozen: false });
export const IeLeasingOrderContext = createContext({
  isActionsDisabled: false
});

const checkedTabs: Record<string, number[]> = {
  draft: [],
  financed: [0, 1, 2]
};

const TABS: { title: string; key: string }[] = [
  {
    key: 'order',
    title: 'Заявка'
  },
  {
    key: 'offer',
    title: 'Предложения'
  },
  {
    key: 'financing',
    title: 'Финансирование'
  }
];

const IeOrder: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { requestId, isCreateMode } = useLeasingRequestId();

  const { dealer } = useAppSelector((state) => state.userInfo);
  const { dicts } = useAppSelector((state) => state.dict);
  const { request, activeTabIndex, refreshToken } = useAppSelector(
    (state) => state.ieLeasingOrder
  );

  const { isCurrentDealerAvailableForLeasing, currentDealerName } =
    useCheckDealerForLeasing();

  const { fetchOrderData, isLoading } = useFetchOrderData();
  const { selectedOffer } = useSelectedOffer();

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  const isBiometricSessionFailed: boolean =
    !!request?.biometricSession &&
    !request.biometricSession.isLoading &&
    (request.biometricSession.isExpired ||
      !request.biometricSession.isValidated);

  const requestDealerName: string =
    dicts?.[DictTypes.Dealers]?.getBy.uuid[safeKey(request?.dealerUuid)]
      ?.name || '';

  const isCurrentDealerEqualsRequestDealer: boolean =
    dealer?.value === request?.dealerUuid;

  const isActionsDisabled: boolean =
    !dealer ||
    !request ||
    !isCurrentDealerEqualsRequestDealer ||
    !isCurrentDealerAvailableForLeasing;

  const isFieldsFrozen: boolean =
    request?.status.code !== 'leasing_draft' ||
    isBiometricSessionFailed ||
    isActionsDisabled;

  const isFinanced: boolean =
    request?.status.code === 'financed' ||
    selectedOffer?.status.code === 'financed';

  const orderStatus: string = isFinanced
    ? 'financed'
    : request?.status.code || '';

  const handleStepCurrentChange = (targetTabIndex: number) => {
    if (
      isCreateMode ||
      isBiometricSessionFailed ||
      !request ||
      request.status.code === 'leasing_draft' ||
      targetTabIndex === -1
    )
      return;

    if (!selectedOffer && targetTabIndex === 2) {
      notification.warning({
        title: 'Выберите предложение лизинга',
        description:
          'Чтобы перейти на данный этап, вам необходимо выбрать предложение лизинга',
        duration: 5
      });
      return;
    }

    dispatch(setActiveTabIndex(targetTabIndex));
  };

  useEffect(() => {
    dispatch(
      setHeaderConfig({
        title: 'Заявка (ИП)',
        helmetTitle: `Лизинг - Заявка (ИП) ${requestId}`,
        hasBackArrow: true
      })
    );

    return () => {
      dispatch(setHeaderConfig({}));
    };
  }, [requestId]);

  useEffect(() => {
    fetchOrderData();
  }, [refreshToken, isCreateMode]);

  useEffect(() => {
    if (!requestId) {
      navigate(`${ROUTES.leasings}?tab=legal-entity`);
      return;
    }

    if (isCreateMode && !isCurrentDealerAvailableForLeasing) {
      notification.error({
        title: `Дилерский центр ${currentDealerName} не доступен для лизинга`,
        description: 'Пожалуйста, обратитесь к администратору'
      });
      navigate(`${ROUTES.leasings}?tab=legal-entity`);
    }
  }, [
    isCreateMode,
    isCurrentDealerAvailableForLeasing,
    currentDealerName,
    requestId
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetIeLeasingOrder());
    };
  }, []);

  if (isLoading) return <AllurLoader fullScreen />;

  return (
    <IeLeasingOrderContext.Provider value={{ isActionsDisabled }}>
      <div className="ie-order">
        <div className="ie-order__steps-wrapper">
          <CustomTab
            activeTab={TABS[activeTabIndex].key}
            setActiveTab={(targetTabKey) =>
              handleStepCurrentChange(
                TABS.findIndex((item) => item.key === targetTabKey)
              )
            }
            tabs={TABS}
            alwaysChecked={
              checkedTabs[orderStatus] ??
              (SELECTED_BANK_STATUSES.includes(orderStatus) ? [0, 1] : [0])
            }
            finishedText={isFinanced ? 'Профинансирован' : undefined}
          />
        </div>

        <SystemNotifications marginFrom="top" />

        <div className="ie-order__container">
          <div className="ie-order__client-info">
            <HeaderInfo onHistoryModalOpenChange={setIsHistoryModalOpen} />
          </div>

          <HorizontalInfo onHistoryModalOpenChange={setIsHistoryModalOpen} />

          {isActionsDisabled && (
            <Alert
              title="Действия не доступны."
              description={
                !isCurrentDealerEqualsRequestDealer
                  ? `Выбранный дилерский центр (${currentDealerName}) не совпадает с дилерским центром (${requestDealerName}), в котором была создана заявка. Работа с заявкой возможна только в дилерском центре, в котором она была создана. Пожалуйста, выберите соответствующий дилерский центр или обратитесь к администратору.`
                  : `Выбранный дилерский центр (${currentDealerName}) не подключён к лизинговой программе. Работа с заявкой в этом дилерском центре невозможна. Пожалуйста, обратитесь к администратору.`
              }
              type="error"
              showIcon
              className="ie-order__alert"
            />
          )}

          {isBiometricSessionFailed && !!request && (
            <Alert
              title={
                request?.biometricSession?.isExpired
                  ? 'Истек срок действия биометрической сессии'
                  : dicts?.[DictTypes.Statuses]?.getBy.code[
                      request?.status.code
                    ]?.name || request?.status.code
              }
              type="error"
              showIcon
              className="ie-order__alert"
            />
          )}

          <div className="ie-order__content">
            {activeTabIndex === 0 && (
              <CreateTabContext.Provider value={{ isFieldsFrozen }}>
                <OrderStep />
              </CreateTabContext.Provider>
            )}
            {activeTabIndex === 1 && <OfferStep />}
            {activeTabIndex === 2 && <FinancingStep />}
          </div>

          <DraftFormResetButton />
        </div>

        <OrderHistory
          historyType="ie-leasing"
          isOpened={isHistoryModalOpen}
          onClose={setIsHistoryModalOpen}
        />
      </div>
    </IeLeasingOrderContext.Provider>
  );
};

export default IeOrder;
